import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';

/** From this width up the side menu is always on screen; below it, a drawer (office.css `@media (max-width: 899px)`). */
export const SIDE_MENU_FROM = 900;

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * The Office's side menu as a drawer, on a screen narrower than 900 px. The header's Menu button opens
 * it, and while it is open it is a modal dialog: focus moves into it and Tab stays there, the page
 * behind is inert and doesn't scroll, and Escape, the backdrop, its close button, any change of page and
 * the window growing to 900 px each close it. Focus then goes back to the Menu button.
 *
 * At 900 px and up the Menu button is hidden (office.css), so the drawer never opens and the side menu
 * is what it always was.
 */
export function useMenuDrawer() {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLElement>(null);
  const { pathname, search } = useLocation();

  // A followed link, the back button, a search: any new page closes it.
  useEffect(() => setOpen(false), [pathname, search]);

  useEffect(() => {
    const root = drawerRef.current;
    if (!open || !root) return;
    const items = () => [...root.querySelectorAll<HTMLElement>(FOCUSABLE)];
    items()[0]?.focus();

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        return;
      }
      if (e.key !== 'Tab') return;
      const list = items();
      const first = list[0];
      const last = list.at(-1);
      if (!first || !last) return;
      const at = document.activeElement;
      if (!root!.contains(at)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (e.shiftKey && at === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && at === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const wide = typeof window.matchMedia === 'function' ? window.matchMedia(`(min-width: ${SIDE_MENU_FROM}px)`) : null;
    const onWide = (e: { matches: boolean }) => {
      if (e.matches) setOpen(false);
    };
    wide?.addEventListener('change', onWide);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      wide?.removeEventListener('change', onWide);
      // However it closed. At 900 px and up the button is hidden, and focus stays where it is.
      buttonRef.current?.focus();
    };
  }, [open]);

  const show = useCallback(() => setOpen(true), []);
  const close = useCallback(() => setOpen(false), []);
  return { open, show, close, buttonRef, drawerRef };
}
